import { useMemo } from 'react'

import { UserTenantRole } from '@tabeeb/enums'
import { RootState, useSelector } from '@tabeeb/store'
import {
  getIsBillingSettingsTenantRoleEnabled,
  getIsViewingAdminTenantRoleEnabled,
  getTenantRoleDisplayName,
} from '@tabeeb/modules/appConfigState/selectors'

import { useApi } from '@tabeeb/modules/shared/utils/API'
import { useQuery } from '@tanstack/react-query'

import { TenantRole } from '../types'
import { AdminUserQueryKeys } from '../queries'

const roleEnabledSelectorMap: Partial<Record<UserTenantRole, (state: RootState) => boolean>> = {
  [UserTenantRole.ViewingAdmin]: getIsViewingAdminTenantRoleEnabled,
  [UserTenantRole.BillingSettingsAdmin]: getIsBillingSettingsTenantRoleEnabled,
}

const useTenantRoles = () => {
  const { mainApi: api } = useApi()

  const query = useQuery({
    queryKey: [AdminUserQueryKeys.Roles],
    queryFn: api.userRoles.getRoles,
  })

  const allRoles = useSelector((state): TenantRole[] =>
    [
      UserTenantRole.SystemAdmin,
      UserTenantRole.TenantAdmin,
      UserTenantRole.BillingAdmin,
      UserTenantRole.SessionAdmin,
      UserTenantRole.User,
      UserTenantRole.Guest,
      UserTenantRole.AIAdmin,
      UserTenantRole.AIUser,
      UserTenantRole.LabelingAdmin,
      UserTenantRole.ViewingAdmin,
      UserTenantRole.BillingSettingsAdmin,
      UserTenantRole.CorporationAdmin,
    ]
      .filter((role) => roleEnabledSelectorMap[role]?.(state) ?? true)
      .map((role) => ({
        Name: role,
        DisplayName: getTenantRoleDisplayName(state, { Role: role }),
      }))
  )

  const rolesUserCanCreate = useMemo(() => {
    return allRoles.filter((role) => query.data?.some((roleName) => roleName === role.Name))
  }, [allRoles, query.data])

  return useMemo(
    () => ({
      loading: query.isFetching,
      allRoles,
      rolesUserCanCreate,
    }),
    [allRoles, rolesUserCanCreate, query.isFetching]
  )
}

export default useTenantRoles
